import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase.js';

interface AuthState {
  session: Session | null;
  loading: boolean;
  signInWithEmail: (email: string) => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, next) => setSession(next));
    // Supabase consumes the magic-link token from the URL hash but leaves a bare
    // "#" (or the spent token fragment) behind — scrub it so the address bar stays clean.
    const h = window.location.hash;
    if (h === '#' || /(?:access_token|error|type)=/.test(h)) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    return () => data.subscription.unsubscribe();
  }, []);

  const value: AuthState = {
    session,
    loading,
    signInWithEmail: async (email) => {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: window.location.origin },
      });
      if (error) throw error;
    },
    // Password sign-in. Accounts are provisioned by hand (admin API) — there is no
    // self-serve signup and deliberately no reset flow, because a reset link would
    // put the account back behind the mailbox that passwords were added to bypass.
    // A forgotten password means an owner issues a new one.
    signInWithPassword: async (email, password) => {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    },
    signInWithGoogle: async () => {
      // OAuth — no email is sent, so it's not subject to the magic-link rate limit.
      // The redirect returns to origin with a code/token the client auto-exchanges
      // (detectSessionInUrl is on by default) → onAuthStateChange sets the session.
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.origin },
      });
      if (error) throw error;
    },
    signOut: async () => {
      await supabase.auth.signOut();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
